/**
 * Key screen: what this browser holds, and the deliberate act of entering.
 *
 * Two rules shape this page:
 *   — entering is always an explicit choice. Nothing connects on page load,
 *     because a console that logs itself back in after F5 has no logout.
 *   — the badge on each key says which protection is actually in force, not
 *     which one sounds best. A Hello prompt that only asks is labelled as
 *     asking; a key encrypted by the authenticator is labelled as encrypted.
 */
import { useActionState, useCallback, useEffect, useRef, useState } from 'react';
import {
  addGate, CredentialError, deleteKey, importCredential, listKeys,
  protectionAvailable, setActiveKey, type KeySummary,
} from './key-store.ts';
import { enterAfterImport, reconnectWithNewKey, signIn } from '../transport/socket.ts';
import { useConnection } from '../transport/hooks.ts';
import type { Protection } from './webauthn.ts';

interface ActionResult {
  error: string | null;
}

const PROTECTION: Record<Protection, { text: string; cls: string; title: string }> = {
  'webauthn-prf': {
    text: 'зашифрован Windows Hello',
    cls: 'tag--ok',
    title: 'Ключ хранится зашифрованным. Без биометрии это просто набор байт.',
  },
  'webauthn-gate': {
    text: 'спрашивает Windows Hello',
    cls: 'tag--accent',
    title:
      'Перед входом запрашивается биометрия, но сам ключ не зашифрован: ' +
      'этот браузер не поддерживает расширение PRF.',
  },
  none: {
    text: 'без подтверждения',
    cls: '',
    title: 'Вход возможен любому, кто получил доступ к этому браузеру.',
  },
};

function when(value: string): string {
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

export function Login(): React.JSX.Element {
  const connection = useConnection();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [keys, setKeys] = useState<KeySummary[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [helloAvailable, setHelloAvailable] = useState(false);
  const [protect, setProtect] = useState(true);
  const [rowError, setRowError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setKeys(await listKeys());
  }, []);

  useEffect(() => {
    void refresh();
    void protectionAvailable().then(setHelloAvailable);
  }, [refresh]);

  const [result, action, pending] = useActionState<ActionResult, FormData>(
    async (_prev, formData) => {
      const raw = String(formData.get('credential') ?? '').trim();
      if (!raw) return { error: 'Вставьте содержимое файла с ключом.' };

      try {
        await importCredential(raw, { protect: protect && helloAvailable });
      } catch (err) {
        if (err instanceof CredentialError) return { error: err.message };
        return { error: `Не удалось импортировать ключ: ${(err as Error).message}` };
      }

      if (textareaRef.current) textareaRef.current.value = '';
      setFileName(null);
      setAdding(false);
      await refresh();
      // Importing is itself an explicit act, so go straight in.
      try {
        await enterAfterImport();
      } catch (err) {
        return { error: (err as Error).message };
      }
      return { error: null };
    },
    { error: null },
  );

  async function onFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    if (textareaRef.current) textareaRef.current.value = text;
  }

  async function onEnter(kid: string): Promise<void> {
    setBusy(kid);
    setRowError(null);
    try {
      await setActiveKey(kid);
      await signIn();
    } catch (err) {
      setRowError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onProtect(kid: string): Promise<void> {
    setBusy(kid);
    setRowError(null);
    try {
      await addGate(kid);
      await refresh();
    } catch (err) {
      setRowError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(kid: string): Promise<void> {
    setBusy(kid);
    setRowError(null);
    await deleteKey(kid);
    await refresh();
    await reconnectWithNewKey();
    setBusy(null);
  }

  const hasKeys = (keys?.length ?? 0) > 0;
  const showForm = adding || (keys != null && keys.length === 0);

  return (
    <main className="login">
      <div className="login__card">
        <h1 className="login__title">Консоль резервного копирования</h1>

        {!hasKeys && (
          <p className="login__lead">
            Входа по паролю здесь нет. Вставьте выданный вам файл с ключом — он
            останется в этом браузере в виде, который невозможно прочитать
            обратно, и будет подтверждать вход.
          </p>
        )}

        {connection.status === 'signed-out' && (
          <p className="alert alert--info" role="status">
            Вы вышли из консоли. Ключ остался в браузере — нажмите «Войти».
          </p>
        )}

        {connection.status === 'locked' && !connection.message && hasKeys && (
          <p className="alert alert--info" role="status">
            {keys?.some((k) => k.active && k.protection === 'webauthn-prf')
              ? 'Ключ зашифрован: после перезагрузки страницы нужно подтвердить вход через Windows Hello.'
              : 'Подтвердите вход, чтобы открыть консоль.'}
          </p>
        )}

        {connection.status === 'auth-failed' && connection.message && (
          <p className="alert alert--error" role="alert">
            Сервер отклонил ключ: {connection.message}
            {connection.rejectedKid && <> Отклонённый ключ отмечен в списке.</>}
          </p>
        )}

        {rowError && <p className="alert alert--error" role="alert">{rowError}</p>}
        {connection.status === 'locked' && connection.message && (
          <p className="alert alert--error" role="alert">{connection.message}</p>
        )}

        {keys == null && <p className="muted">Читаю ключи из браузера…</p>}

        {hasKeys && (
          <section className="keys">
            <h2 className="keys__title">Ключи в этом браузере</h2>
            <ul className="keys__list">
              {keys!.map((k) => {
                const rejected = connection.rejectedKid === k.kid;
                const p = PROTECTION[k.protection];
                return (
                  <li key={k.kid} className={`keys__item${rejected ? ' keys__item--rejected' : ''}`}>
                    <div className="keys__info">
                      <span className="keys__label">
                        {k.label}
                        <span className={`tag ${p.cls}`} title={p.title}>{p.text}</span>
                        {rejected && <span className="tag tag--danger">отклонён сервером</span>}
                      </span>
                      <span className="keys__meta">
                        {k.owner} · <span className="mono">{k.kid}</span> · добавлен {when(k.importedAt)}
                      </span>
                    </div>
                    <div className="keys__actions">
                      <button
                        type="button"
                        className="button button--primary"
                        disabled={busy === k.kid || pending}
                        onClick={() => void onEnter(k.kid)}
                      >
                        {busy === k.kid
                          ? '…'
                          : k.protection === 'none'
                            ? 'Войти'
                            : 'Войти через Hello'}
                      </button>
                      {k.protection === 'none' && helloAvailable && (
                        <button
                          type="button"
                          className="button"
                          disabled={busy === k.kid}
                          onClick={() => void onProtect(k.kid)}
                          title="Запрашивать Windows Hello перед входом"
                        >
                          Защитить
                        </button>
                      )}
                      <button
                        type="button"
                        className="button button--danger"
                        disabled={busy === k.kid || pending}
                        onClick={() => void onDelete(k.kid)}
                      >
                        Удалить
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="muted keys__note">
              Удаление убирает ключ только из этого браузера. Если устройство
              потеряно, ключ нужно ещё и отозвать на сервере — в разделе
              «Ключи доступа» внутри консоли.
              {helloAvailable && (
                <>
                  {' '}Полное шифрование ключа биометрией выбирается при добавлении:
                  для уже добавленного ключа приватная часть недоступна даже нам,
                  поэтому кнопка «Защитить» лишь добавляет запрос Hello перед входом.
                </>
              )}
            </p>

            {!showForm && (
              <button type="button" className="button" onClick={() => setAdding(true)}>
                Добавить ещё один ключ
              </button>
            )}
          </section>
        )}

        {showForm && (
          <form action={action} className="login__form">
            {hasKeys && <h2 className="keys__title">Новый ключ</h2>}

            <label className="field">
              <span className="field__label">Файл ключа</span>
              <input type="file" accept="application/json,.json" onChange={onFile} className="field__file" />
              {fileName && <span className="field__hint">Загружен: {fileName}</span>}
            </label>

            <label className="field">
              <span className="field__label">…или вставьте содержимое</span>
              <textarea
                ref={textareaRef}
                name="credential"
                rows={hasKeys ? 6 : 10}
                spellCheck={false}
                autoComplete="off"
                placeholder={'{\n  "format": "ebk-credential/1",\n  "kid": "own-…",\n  …\n}'}
                className="field__textarea"
              />
            </label>

            {helloAvailable ? (
              <label className="check">
                <input type="checkbox" checked={protect} onChange={(e) => setProtect(e.target.checked)} />
                <span>
                  Защитить ключ через Windows Hello
                  <span className="check__hint">
                    Где браузер это умеет, ключ будет храниться зашифрованным и без
                    отпечатка или PIN станет бесполезным набором байт.
                  </span>
                </span>
              </label>
            ) : (
              <p className="muted">
                На этом устройстве нет встроенного аутентификатора (Windows Hello,
                Touch ID), поэтому ключ будет храниться без биометрической защиты.
              </p>
            )}

            {result.error && <p className="alert alert--error" role="alert">{result.error}</p>}

            <div className="login__buttons">
              <button type="submit" className="button button--primary" disabled={pending}>
                {pending ? 'Проверяю ключ…' : hasKeys ? 'Добавить ключ' : 'Войти'}
              </button>
              {hasKeys && (
                <button type="button" className="button" onClick={() => setAdding(false)}>
                  Отмена
                </button>
              )}
            </div>
          </form>
        )}

        <p className="login__note">
          После успешного входа удалите файл с ключом: он содержит полную
          приватную часть.
        </p>
      </div>
    </main>
  );
}
